
import React from "react";
import { Helmet } from "react-helmet-async";
import Layout from "../components/Layout";
import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

// Blog post data structure
const blogPosts = [
  {
    id: "choosing-right-tablet",
    title: "How to Choose the Best Tablet for Your Business in 2025",
    excerpt: "With numerous options available, selecting the right tablet for your business operations can be challenging. This guide outlines key factors to consider...",
    date: "May 2, 2025",
    image: "https://images.unsplash.com/photo-1544244015-0df4b3ffc6b0?w=600&auto=format&fit=crop",
    category: "Tablets",
    author: "Arjun Kapoor",
    readTime: "7 min read"
  },
  {
    id: "industrial-tablets-manufacturing",
    title: "Why Indian Manufacturers Are Leading in Custom Gadgets",
    excerpt: "The rise of Indian electronics manufacturers in the global market has been remarkable. Learn how companies like Truecore are revolutionizing the custom hardware landscape...",
    date: "April 25, 2025",
    image: "https://images.unsplash.com/photo-1593508512255-86ab42a8e620?w=600&auto=format&fit=crop",
    category: "Industry Insights",
    author: "Priya Sharma",
    readTime: "9 min read"
  },
  {
    id: "custom-pc-business",
    title: "Top 5 Reasons Your Business Needs Custom PCs",
    excerpt: "Off-the-shelf solutions rarely meet the specialized needs of modern businesses. Discover how custom-built PCs can significantly improve productivity and ROI...",
    date: "April 18, 2025",
    image: "https://images.unsplash.com/photo-1547082299-de196ea013d6?w=600&auto=format&fit=crop",
    category: "Custom PCs",
    author: "Vikram Mehta",
    readTime: "5 min read"
  },
  {
    id: "desktop-computers-2025",
    title: "Desktop Computers for Business: 2025 Buyer's Guide",
    excerpt: "Despite the rise of mobile computing, desktop computers remain essential for many business operations. This comprehensive guide helps you navigate the latest options...",
    date: "April 10, 2025",
    image: "https://images.unsplash.com/photo-1624705002806-5d72df19c3ad?w=600&auto=format&fit=crop",
    category: "Desktops",
    author: "Neha Gupta",
    readTime: "8 min read"
  }
];

const Blog = () => {
  return (
    <Layout>
      <Helmet>
        <title>Electronics & Technology Blog | Truecore Gadgets India</title>
        <meta name="description" content="Latest insights on custom electronics, tablets, PCs, and technology trends for businesses in India. Expert tips from trusted gadget brand Truecore." />
        <meta name="keywords" content="electronics blog India, technology trends 2025, custom PCs India, tablets for business, desktop computers guide, electronics manufacturer insights, top electronics brand 2025" />
        <link rel="canonical" href="https://truecoregadgets.com/blog" />
        
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Electronics & Technology Blog | Truecore Gadgets" />
        <meta property="og:description" content="Latest insights on custom electronics, tablets, PCs, and technology trends for Indian businesses." />
        <meta property="og:url" content="https://truecoregadgets.com/blog" />
        <meta property="og:image" content="/lovable-uploads/461a823a-5781-4980-ad39-ffec726a88a3.png" />
      </Helmet>

      <section className="container mx-auto py-16 px-4">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h1 className="text-4xl font-bold mb-6 gradient-text">Technology Insights & Guides</h1>
          <p className="text-xl text-muted-foreground">
            Expert advice, industry trends, and practical guides from India's trusted electronics innovator
          </p>
        </div>

        {/* Featured Post */}
        <div className="mb-16">
          <div className="bg-white rounded-xl overflow-hidden shadow-lg border border-gray-100">
            <div className="grid grid-cols-1 lg:grid-cols-2">
              <div className="relative h-64 lg:h-auto">
                <img 
                  src="https://images.unsplash.com/photo-1551645120-d70bfe84c826?w=800&auto=format&fit=crop" 
                  alt="Understanding Enterprise Hardware Requirements" 
                  className="absolute inset-0 w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
              <div className="p-8">
                <div className="flex items-center mb-4">
                  <span className="bg-primary/10 text-primary text-xs font-semibold px-3 py-1 rounded-full">Featured</span>
                  <span className="ml-3 text-sm text-gray-500">May 5, 2025</span>
                </div>
                <h2 className="text-2xl font-bold mb-4">Understanding Enterprise Hardware Requirements for 2025</h2>
                <p className="text-gray-600 mb-6">
                  As technology evolves, so do the requirements for enterprise hardware. This comprehensive guide explores the critical factors businesses should consider when upgrading their tech infrastructure in 2025.
                </p>
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
                      <span className="font-semibold text-sm">SK</span>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-medium">Sunita Krishnan</p>
                      <p className="text-xs text-gray-500">CTO, Truecore Gadgets</p>
                    </div>
                  </div>
                  <span className="text-sm text-gray-500">12 min read</span>
                </div>
                <Link 
                  to="/blog/enterprise-hardware-2025" 
                  className="mt-6 inline-flex items-center text-primary font-medium hover:underline"
                >
                  Read Article
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Blog Post Grid */}
        <h2 className="text-2xl font-bold mb-8">Latest Articles</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {blogPosts.map((post) => (
            <div key={post.id} className="bg-white rounded-xl overflow-hidden shadow-md border border-gray-100 transition-all duration-300 hover:shadow-lg">
              <div className="h-48 overflow-hidden">
                <img 
                  src={post.image} 
                  alt={post.title} 
                  className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                  loading="lazy"
                />
              </div>
              <div className="p-6">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-medium bg-muted px-2 py-1 rounded">{post.category}</span>
                  <span className="text-xs text-gray-500">{post.date}</span>
                </div>
                <h3 className="text-xl font-semibold mb-3">{post.title}</h3>
                <p className="text-gray-600 text-sm mb-4">{post.excerpt}</p>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">{post.author}</span>
                  <span className="text-xs text-gray-500">{post.readTime}</span>
                </div>
                <Link 
                  to={`/blog/${post.id}`} 
                  className="mt-4 inline-flex items-center text-primary text-sm font-medium hover:underline"
                >
                  Read More
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </div>
            </div>
          ))}
        </div>

        {/* Subscribe Section */}
        <div className="mt-16 bg-gradient-to-r from-primary/10 to-secondary/10 rounded-xl p-8 text-center">
          <h2 className="text-2xl font-bold mb-4">Stay Updated with Industry Insights</h2>
          <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
            Subscribe to our newsletter for the latest technology trends, product updates, and exclusive guides directly to your inbox.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
            <input 
              type="email" 
              placeholder="Your email address" 
              className="flex-grow px-4 py-2 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary"
            />
            <button className="bg-primary text-white px-6 py-2 rounded-md hover:bg-primary/90 transition-colors">
              Subscribe
            </button>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Blog;
